/**
 * Infinite scroll per le pagine collection - luxgentleman.com
 * Aggiunta custom al tema Prestige 10.10.0 (creata il 2026-09-04).
 *
 * Progressive enhancement: la paginazione classica resta nel DOM (nascosta) sia come
 * fallback se il JS non parte, sia perche' i crawler continuino a seguire i link.
 * Il custom element si ricostruisce da solo quando Prestige rimpiazza la sezione
 * dopo un filtro, perche' la logica sta tutta in connectedCallback().
 */
(() => {
  if (customElements.get('infinite-scroll-collection')) return;

  class InfiniteScrollCollection extends HTMLElement {
    connectedCallback() {
      this.list = document.querySelector(this.getAttribute('product-list') || '');
      this.nav = this.querySelector('nav.pagination');
      this.controls = this.querySelector('[data-infinite-controls]');
      this.button = this.querySelector('[data-infinite-button]');
      this.status = this.querySelector('[data-infinite-status]');

      // Senza uno di questi si resta sulla paginazione classica.
      if (!this.list || !this.nav || !this.controls || !('IntersectionObserver' in window)) return;

      this.loading = false;
      this.failures = 0;
      this.nav.hidden = true;
      this.controls.hidden = false;

      this.button.addEventListener('click', () => this.loadNext());
      this.setupSentinel();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    get nextUrl() {
      const link = this.nav && this.nav.querySelector('a[rel="next"]');
      return link ? link.href : null;
    }

    setupSentinel() {
      if (!this.nextUrl) {
        this.controls.hidden = true;
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) this.loadNext();
        },
        // parte con un po' di anticipo, cosi' il caricamento e' gia' pronto
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this.controls);
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text || '';
    }

    async loadNext() {
      const url = this.nextUrl;
      if (this.loading || !url) return;

      this.loading = true;
      this.button.disabled = true;
      this.setStatus(this.dataset.loadingText || '');

      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

        const newList = doc.querySelector(this.getAttribute('product-list'));
        if (!newList) throw new Error('product-list non trovata nella pagina successiva');

        const fragment = document.createDocumentFragment();
        newList.querySelectorAll(':scope > *').forEach((card) => {
          // l'animazione di comparsa dipende da un observer che non copre i nodi
          // aggiunti a mano: senza questo le card resterebbero invisibili
          card.removeAttribute('reveal-on-scroll');
          fragment.appendChild(document.importNode(card, true));
        });
        this.list.appendChild(fragment);

        // la nuova paginazione porta il link alla pagina ancora successiva
        const newNav = doc.querySelector('nav.pagination');
        if (newNav) {
          newNav.hidden = true;
          this.nav.replaceWith(newNav);
          this.nav = newNav;
        } else {
          this.nav.innerHTML = '';
        }

        this.failures = 0;
        this.setStatus('');

        if (!this.nextUrl) {
          if (this.observer) this.observer.disconnect();
          this.controls.hidden = true;
          this.setStatus(this.dataset.endText || '');
        }
      } catch (err) {
        console.error('[infinite-scroll]', err);
        this.failures += 1;
        // dopo due errori si smette di riprovare da soli e si torna alla paginazione
        if (this.failures >= 2) {
          if (this.observer) this.observer.disconnect();
          this.nav.hidden = false;
          this.controls.hidden = true;
        } else {
          this.setStatus(this.dataset.errorText || '');
        }
      } finally {
        this.loading = false;
        this.button.disabled = false;
      }
    }
  }

  customElements.define('infinite-scroll-collection', InfiniteScrollCollection);
})();
